import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';

import { useTranslation } from 'react-i18next';
import { Button, Col, Divider, Row, Typography } from 'antd';

import { Card } from 'src/components/custom';
import { NumberFormatter } from 'src/utils/number';
import { PATH as PATH_QUOTATION } from 'src/pages/quotation';
import history from 'src/libs/history';
import { PATH as PATH_ORDER } from 'src/pages/order';
import { SIMPLE_DATE_FORMAT, SORT } from 'src/constants/app';
import LocalStorage from 'src/utils/LocalStorage';

import { DISPLAY_OF_TOTAL_AMOUNT, UNCHIN_SETTEI_TYPE } from 'src/constants/app';

type Props = {
  preOrderDetail: {
    orderClosingTime: string;
    unchinSettei: string;
    kingakuHyoji: string;
  };
  preOrderData: any;
  exportPDF: any;
};

const CalculateResultTable: React.FC<Props> = ({
  preOrderDetail,
  preOrderData,
  exportPDF,
}) => {
  const location = useLocation();
  const { t } = useTranslation();

  const numberOfRecord = preOrderData?.length || 0;

  const totals = preOrderData?.reduce(
    (acc, item) => {
      return {
        marumeMitrGaku: acc.marumeMitrGaku.plus(item.marumeMitrGaku || 0),
        marumeSogaku: acc.marumeSogaku.plus(item.marumeSogaku || 0),
        marumeJury: acc.marumeJury.plus(parseFloat(item.marumeJury) || 0),
      };
    },
    {
      marumeMitrGaku: new Decimal(0),
      marumeSogaku: new Decimal(0),
      marumeJury: new Decimal(0),
    },
  );

  const totalAmount = Number(totals?.marumeSogaku) || 0;
  const totalWeight = Number(totals?.marumeJury) || 0;
  const totalAmountInCludedTax = Number(totals?.marumeMitrGaku) || 0;
  const showPriceIncludingTax =
    preOrderDetail.kingakuHyoji === DISPLAY_OF_TOTAL_AMOUNT.TAX_INCLUDED;

  const showShippingFee =
    preOrderDetail.unchinSettei === UNCHIN_SETTEI_TYPE.TOTAL_TREATMENT;

  const handleOrder = async () => {
    history.push(PATH_ORDER, {
      items: preOrderData,
      search: location.search,
    });
  };

  const handleExportPDF = async () => {
    const newErrors: string[] = [];
    if (preOrderData.length > 0) {
      await exportPDF.mutate({
        seqnos: preOrderData.map((item) => Number(item.seqno)),
        gengoCd: LocalStorage.lang,
        sortColumn: 'webMitrNo',
        sortDirection: SORT.ASC,
      });
    } else {
      newErrors.push(
        t('message.E000021', {
          param: t('quotation_page.detail'),
        }),
      );
    }
  };

  return (
    <div className="mb-5 mt-5 order-page">
      <Card title="">
        <Row>
          <Col xs={24} sm={24} md={9} className="d-flex align-center">
            <Row className="d-flex-1">
              <Typography.Title level={5} className="sm-flex-1 text-title">
                {t('multiple_quotes_page.total_record')}
              </Typography.Title>
              <Typography.Title className="mt-0 highlight text-value" level={5}>
                {numberOfRecord}&nbsp;
              </Typography.Title>
              <Typography.Title className="mt-0 text-value" level={5}>
                {t('multiple_quotes_page.point')}
              </Typography.Title>
            </Row>
          </Col>
          <Divider className="divider" />
          <Col xs={24} sm={24} md={9} className="d-flex align-center">
            <Row className="d-flex-1">
              <Typography.Title level={5} className="sm-flex-1 text-title">
                {t('multiple_quotes_page.gross_weight')}
              </Typography.Title>
              <Typography.Title className="mt-0 highlight text-value" level={5}>
                {NumberFormatter.format(totalWeight)}
              </Typography.Title>
              <Typography.Title className="mt-0 text-value" level={5}>
                {preOrderData?.[0]?.marumeJury?.split(' ')[1]}
              </Typography.Title>
            </Row>
          </Col>
          <Divider className="divider" />
        </Row>
        <Row className="mt-16 mt_16">
          <Col
            xs={24}
            sm={24}
            md={9}
            className="d-flex align-center margin-sm margin_sm"
          >
            <Row className="d-flex-1">
              <Typography.Title level={5} className="sm-flex-1 text-title">
                {t('multiple_quotes_page.total_price_exclude_tax')}
              </Typography.Title>
              <Typography.Title className="mt-0 highlight text-value" level={5}>
                {preOrderData?.[0]?.currency
                  ? `${NumberFormatter.format(totalAmount, 18)}(${
                      preOrderData?.[0]?.currency
                    })`
                  : `${NumberFormatter.format(totalAmount, 18)}`}
              </Typography.Title>
            </Row>
          </Col>
          <Divider className="divider" />
          {showPriceIncludingTax && (
            <Col xs={24} sm={24} md={9} className="d-flex align-center">
              <Row className="d-flex-1">
                <Typography.Title level={5} className="sm-flex-1 text-title">
                  {t('multiple_quotes_page.total_price_include_tax')}
                </Typography.Title>
                <Typography.Title
                  className="mt-0 highlight text-value"
                  level={5}
                >
                  {preOrderData?.[0]?.currency
                    ? `${NumberFormatter.format(totalAmountInCludedTax, 18)}(${
                        preOrderData?.[0]?.currency
                      })`
                    : `${NumberFormatter.format(totalAmountInCludedTax, 18)}`}
                </Typography.Title>
              </Row>
            </Col>
          )}
          <Divider className="divider" />
          {showShippingFee && (
            <Col xs={24} sm={24} md={6} className="d-flex align-center">
              <Row className="d-flex-1">
                <Typography.Title level={5} className="sm-flex-1 text-title">
                  {t('multiple_quotes_page.fare')}
                </Typography.Title>
                <Typography.Title
                  className="mt-0 highlight text-value"
                  level={5}
                >
                  {preOrderData?.[0]?.currency
                    ? `${NumberFormatter.format(
                        preOrderData?.[0]?.shoUnchn,
                        18,
                      )}(${preOrderData?.[0]?.currency})`
                    : `${NumberFormatter.format(
                        preOrderData?.[0]?.shoUnchn,
                        18,
                      )}`}
                </Typography.Title>
              </Row>
            </Col>
          )}
          <Divider className="divider" />
        </Row>
        <Row className="mt-16 mt_16">
          <Col
            xs={24}
            sm={24}
            md={9}
            className="d-flex align-center margin-sm margin_sm"
          >
            <Row className="d-flex-1">
              <Typography.Title level={5} className="sm-flex-1 text-title">
                {t('multiple_quotes_page.order_closing_time')}
              </Typography.Title>
              <Typography.Title className="mt-0 highlight text-value" level={5}>
                {`${dayjs().format(SIMPLE_DATE_FORMAT)} ${
                  preOrderDetail.orderClosingTime
                }`}
              </Typography.Title>
            </Row>
          </Col>
          <Divider className="divider" />
        </Row>
        <Row style={{ marginTop: 20 }}>
          <Col xs={24} sm={24} md={24}>
            <Row
              justify="center"
              gutter={2}
              className="container_multiple_quotes_page"
            >
              <Col>
                <Button
                  className="btn-custom btn_pdf_output"
                  type="primary"
                  style={{ minWidth: 187, marginRight: 20 }}
                  icon={<FilePdfOutlined />}
                  onClick={handleExportPDF}
                >
                  {t('multiple_quotes_page.pdf_output')}
                </Button>
              </Col>
              <Col>
                <Button
                  className="btn-custom btn_order_now"
                  type="primary"
                  style={{ minWidth: 187, marginRight: 20 }}
                  onClick={handleOrder}
                >
                  {t('multiple_quotes_page.order_now')}
                </Button>
              </Col>
              <Col>
                <Link to={PATH_QUOTATION}>
                  <Button
                    className="btn-custom outline btn_estimate_history"
                    type="primary"
                    style={{ minWidth: 187 }}
                  >
                    {t('multiple_quotes_page.estimate_history_button')}
                  </Button>
                </Link>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default CalculateResultTable;
